import React, { useContext, useEffect, useState } from "react";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import cx from "classnames";
import { supabase } from "../../supabaseClient";
import { AuthContext } from "../../AuthProvider";
import { Feed as FeedType } from "../../types/api/Feed";
import { handleError } from "../../utils/handleError";
import { CheckCircle, XCircle } from "react-feather";
import { Meta } from "./Feeds/Meta";
import { Threads } from "./Feeds/Threads";
import { Requirements, RequirementsType } from "./Requirements";
import { useNavigate } from "react-router-dom";
import { FlexAlign } from "../_ui/FlexAlign";
import { Bluesky } from "./Feeds/Bluesky";
import { Mastodon } from "./Feeds/Mastodon";

type FeedUpdate = Pick<
  FeedType,
  | "slug"
  | "name"
  | "auto_publish"
  | "webhook"
  | "user_id"
  | "instance_url"
  | "auto_post_as"
  | "post_template_override"
> & {
  requirements: RequirementsType;
};

export const Feed = (props: { item?: Partial<FeedType>; isNew?: boolean }) => {
  const { item, isNew } = props;
  const { session } = useContext(AuthContext);
  const navigate = useNavigate();
  const [apiError, setApiError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [availableFeeds, setAvailableFeeds] = useState<
    Pick<FeedType, "id" | "name" | "slug">[]
  >([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    // @ts-expect-error
  } = useForm({
    values: isNew ? undefined : processRequirementsDefaults(item ?? {}),
    defaultValues: isNew ? undefined : processRequirementsDefaults(item ?? {}),
  });

  // Load available feeds for auto_post_as dropdown
  useEffect(() => {
    const loadFeeds = async () => {
      try {
        const { data: feeds, error } = await supabase
          .from("feeds")
          .select("id, name, slug")
          .neq("id", item?.id || 0) // Exclude current feed
          .order("name");

        if (!error && feeds) {
          setAvailableFeeds(feeds);
        }
      } catch (error) {
        console.error("Error loading feeds:", error);
      }
    };

    loadFeeds();
  }, [session?.user?.id, item?.id]);

  const onSubmit: SubmitHandler<FieldValues> = async (content) => {
    setUpdating(true);
    setApiError(null);

    console.log("CONTENT", content);

    const formatted: FeedUpdate = {
      slug: content.slug,
      name: content.name,
      auto_publish: content.auto_publish,
      auto_post_as: content.auto_post_as || null,
      webhook: content.webhook,
      instance_url: content.instance_url,
      user_id: session?.user.id || null,
      post_template_override: content.post_template_override || null,
      requirements: processRequirements(content),
    };

    if (content.max_length === "") {
      delete content.max_length;
    }

    const { data, error } = isNew
      ? await supabase
          .from("feeds")
          .insert(formatted as FeedType)
          .select()
      : await supabase
          .from("feeds")
          .update(formatted as FeedType)
          .eq("id", item?.id!)
          .select();

    if (error) {
      handleError(error);
    }

    if (!data) return;
    setApiError(apiError);

    if (isNew) {
      reset();
    }

    setUpdating(false);

    navigate("/feeds");
  };

  const deleteItem = async () => {
    setDeleting(true);
    setApiError(null);

    await supabase.from("feeds").delete().eq("id", item?.id!);

    navigate("/feeds");
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="card w-full bg-base-100 mb-8">
          <div className="card-body">
            {!isNew && (
              <div className="prose">
                <h3 className="card-title my-0">Feed Settings</h3>
              </div>
            )}

            <div className="form-control w-full mb-2">
              <label className="label">
                <span className="label-text">Name</span>
              </label>
              <input
                placeholder="Name"
                {...register("name", { required: true })}
                className="input input-lg w-full"
              />
              {errors.name && <span>This field is required</span>}
            </div>

            <div className="form-control w-full mb-2">
              <label className="label">
                <span className="label-text">Slug</span>
              </label>
              <input
                placeholder="slug"
                {...register("slug", { required: true })}
                className="input input-lg w-full"
              />
              {errors.name && <span>This field is required</span>}
            </div>
          </div>
        </div>
        <div className="card w-full bg-base-100 mb-8">
          <div className="card-body">
            <div className="prose">
              <h3 className="card-title my-0">Auto-Publish</h3>
            </div>

            <div className="form-control mb-2">
              <label className="label">
                <input
                  type="checkbox"
                  {...register("auto_publish")}
                  className="toggle toggle-xl"
                />
                Auto publish
              </label>
            </div>

            {watch("auto_publish") && (
              <>
                {/** SOCIAL MEDIA AUTH HANDLERS */}

                {(watch("slug") === "facebook_api" ||
                  watch("slug") === "instagram_api") && <Meta />}
                {watch("slug") === "threads_api" && <Threads />}
                {watch("slug") === "bluesky_api" && (
                  <Bluesky register={register} watch={watch} />
                )}
                {(watch("slug") === "mastodon_api" ||
                  watch("auto_post_as") === "mastodon_api") && (
                  <Mastodon register={register} watch={watch} />
                )}

                {/** END SOCIAL MEDIA AUTH HANDLERS */}

                <fieldset className="fieldset bg-base-200 border-base-300 rounded-box border p-4">
                  <legend className="fieldset-legend">
                    Auto Post As (Optional)
                  </legend>
                  <select
                    {...register("auto_post_as")}
                    className="select select-bordered w-full"
                  >
                    <option value="">Use this feed's configuration</option>
                    {availableFeeds.map((feed) => (
                      <option
                        key={feed.id}
                        value={feed.slug || ""}
                        selected={feed.slug === watch("auto_post_as")}
                      >
                        {feed.name} ({feed.slug})
                      </option>
                    ))}
                  </select>

                  <p className="label">
                    Select another feed to use its authentication credentials
                    for posting.
                  </p>
                </fieldset>

                <fieldset className="fieldset bg-base-200 border-base-300 rounded-box border p-4">
                  <legend className="fieldset-legend">
                    Post Template Override (Optional)
                  </legend>
                  <textarea
                    placeholder="Override the default post template for this feed..."
                    {...register("post_template_override")}
                    className="textarea textarea-bordered w-full h-24"
                    rows={3}
                  />

                  <p className="label">
                    Leave empty to use your profile's default post template
                  </p>
                </fieldset>

                <fieldset className="fieldset bg-base-200 border-base-300 rounded-box border p-4">
                  <legend className="fieldset-legend">
                    Custom Implementation
                  </legend>
                  <label className="label">
                    <span className="label-text">Webhook</span>
                  </label>
                  <input
                    placeholder="Url of the webhook"
                    {...register("webhook")}
                    className="input w-full"
                  />

                  <p className="label">
                    Use this option if you want to build your own implementation
                    or if a default implementation doesn't exist.
                  </p>
                </fieldset>
              </>
            )}
          </div>
        </div>

        <div className="card w-full bg-base-100 mb-8">
          <div className="card-body">
            <div className="prose">
              <h3 className="card-title my-0">Posts Requirements</h3>
            </div>
            <Requirements
              register={register}
              defaultValues={item?.requirements as RequirementsType}
            />
          </div>
        </div>

        <FlexAlign>
          {!isNew && (
            <button
              type="button"
              className={cx("btn btn-outline btn-error")}
              onClick={() => setShowDeleteModal(true)}
            >
              {deleting ? (
                <span className="loading loading-spinner"></span>
              ) : (
                <XCircle />
              )}
              Delete
            </button>
          )}

          <button type="submit" className={cx("btn btn-lg btn-primary")}>
            {updating ? (
              <span className="loading loading-spinner"></span>
            ) : (
              <CheckCircle />
            )}
            {isNew ? "Add" : "Update"}
          </button>
        </FlexAlign>
      </form>

      <dialog className={`modal ${showDeleteModal ? "modal-open" : ""}`}>
        <div className="modal-box">
          <h3 className="font-bold text-lg">Delete Feed</h3>
          <p className="py-4">
            Are you sure you want to delete this feed? This action cannot be
            undone.
          </p>
          <div className="modal-action">
            <button className="btn" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </button>
            <button
              className="btn btn-error"
              onClick={() => {
                setShowDeleteModal(false);
                deleteItem();
              }}
            >
              Delete
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowDeleteModal(false)}>close</button>
        </form>
      </dialog>
    </>
  );
};

const processRequirements = (content: FieldValues): RequirementsType => {
  return {
    TEXT: {
      length: parseInt(content.requirements?.TEXT?.length),
    },
    PHOTO: {
      minetype: (content.requirements?.PHOTO?.minetype ?? "")
        ?.split(",")
        .map((m: string) => m.trim()),
      max_filesize: parseInt(content.requirements?.PHOTO?.max_filesize),
      max_items: parseInt(content.requirements?.PHOTO?.max_items),
      min_aspect_ratio: parseOptionalNumber(
        content.requirements?.PHOTO?.min_aspect_ratio,
      ),
      max_aspect_ratio: parseOptionalNumber(
        content.requirements?.PHOTO?.max_aspect_ratio,
      ),
      max_resolution: parseOptionalNumber(
        content.requirements?.PHOTO?.max_resolution,
      ),
      overrides: Object.fromEntries(
        Object.entries(content.requirements?.PHOTO?.overrides || {}).map(
          ([type, override]: [string, any]) => [
            type,
            {
              minetype: override.minetype
                ?.split(",")
                .map((m: string) => m.trim()),
              max_filesize: parseInt(override.max_filesize),
            },
          ],
        ),
      ),
    },
    VIDEO: {
      minetype: (content.requirements?.VIDEO?.minetype ?? "")
        ?.split(",")
        .map((m: string) => m.trim()),
      max_filesize: parseInt(content.requirements?.VIDEO?.max_filesize),
      max_items: parseInt(content.requirements?.VIDEO?.max_items),
      min_duration: parseInt(content.requirements?.VIDEO?.min_duration),
      max_duration: parseInt(content.requirements?.VIDEO?.max_duration),
      overrides: Object.fromEntries(
        Object.entries(content.requirements?.VIDEO?.overrides || {}).map(
          ([type, override]: [string, any]) => [
            type,
            {
              minetype: (override.minetype ?? "")
                ?.split(",")
                .map((m: string) => m.trim()),
              max_duration: parseInt(override.max_duration),
              max_filesize: parseInt(override.max_filesize),
              min_duration: parseInt(override.min_duration),
            },
          ],
        ),
      ),
    },
    total_max_items: parseInt(content.requirements?.total_max_items),
  };
};

const parseOptionalNumber = (value: unknown) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const processRequirementsDefaults = (feed: Partial<FeedType>) => {
  const requirements = feed.requirements as RequirementsType;

  return {
    ...feed,
    // Set default instance_url based on slug if not already set
    instance_url: feed.instance_url,
    post_template_override: feed.post_template_override || "",
    requirements: {
      TEXT: {
        length: requirements?.TEXT?.length || 150,
      },
      PHOTO: {
        minetype:
          (requirements?.PHOTO?.minetype ?? []).join(", ") ||
          "image/jpeg, image/jpg, image/png",
        max_filesize: requirements?.PHOTO?.max_filesize || 20,
        max_items: requirements?.PHOTO?.max_items || 10,
        min_aspect_ratio: requirements?.PHOTO?.min_aspect_ratio,
        max_aspect_ratio: requirements?.PHOTO?.max_aspect_ratio,
        max_resolution: requirements?.PHOTO?.max_resolution,
        overrides: Object.fromEntries(
          Object.entries(requirements?.PHOTO?.overrides || {}).map(
            ([type, override]: [string, any]) => [
              type,
              {
                minetype:
                  (override?.PHOTO?.minetype ?? []).join(", ") ||
                  "image/jpeg, image/jpg, image/png",
                max_filesize: override?.PHOTO?.max_filesize || 20,
                max_items: override?.PHOTO?.max_items || 10,
              },
            ],
          ),
        ),
      },
      VIDEO: {
        minetype:
          (requirements?.VIDEO?.minetype ?? []).join(", ") ||
          "video/mp4, video/mov",
        max_filesize: requirements?.VIDEO?.max_filesize || 1024 * 1024 * 50,
        max_items: requirements?.VIDEO?.max_items,
        min_duration: requirements?.VIDEO?.min_duration || 3,
        max_duration: requirements?.VIDEO?.max_duration || 60 * 10,
        overrides: Object.fromEntries(
          Object.entries(requirements?.VIDEO?.overrides || {}).map(
            ([type, override]: [string, any]) => [
              type,
              {
                minetype:
                  (override?.VIDEO?.minetype ?? []).join(", ") ||
                  "video/mp4, video/mov",
                max_filesize: override?.VIDEO?.max_filesize || 1024 * 1024 * 50,
                max_items: override?.VIDEO?.max_items,
                min_duration: override?.VIDEO?.min_duration || 3,
                max_duration: override?.VIDEO?.max_duration || 60 * 10,
              },
            ],
          ),
        ),
      },
      total_max_items: requirements?.total_max_items || 10,
    },
  };
};
